package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.AppEffects
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.displaySmall
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import org.jetbrains.compose.web.css.*

@Composable
fun AdminCard(
    modifier: Modifier = Modifier,
    padding: Int = 24,
    accent: Boolean = false,
    tinted: Boolean = false,
    feature: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    Box(
        modifier
            .fillMaxWidth()
            .sBorderRadius(24)
            .styleModifier {
                when {
                    feature -> {
                        property("background", "linear-gradient(135deg,#0E1A33,#0B1220)")
                        property("color", "#F0F4FC")
                        property("border", "1px solid rgba(255,255,255,0.08)")
                        property(
                            "box-shadow",
                            "0 1px 2px rgba(0,0,0,0.35), 0 12px 32px -16px rgba(0,0,0,0.60)"
                        )
                    }
                    tinted -> {
                        property("background", "rgba(255,255,255,0.03)")
                        property("border", "1px solid rgba(255,255,255,0.06)")
                    }
                    else -> {
                        property("background", "rgba(255,255,255,0.04)")
                        property("border", "1px solid rgba(255,255,255,0.08)")
                        property("backdrop-filter", "blur(14px)")
                        property("-webkit-backdrop-filter", "blur(14px)")
                    }
                }
                if (accent) {
                    property("border", "1px solid rgba(0,97,255,0.45)")
                }
                property("position", "relative")
                property("overflow", "hidden")
            }
    ) {
        Column(Modifier.fillMaxWidth().sPadding(padding)) {
            content()
        }
    }
}

// TODO Q6: ChartCard (uses AdminCard) still called from BehaviorChartsSection, Events, Subscriptions — migrate when those pages are redesigned.
@Composable
fun ChartCard(
    title: String,
    subtitle: String? = null,
    eyebrowText: String? = null,
    modifier: Modifier = Modifier,
    feature: Boolean = false,
    tinted: Boolean = false,
    actions: (@Composable RowScope.() -> Unit)? = null,
    metaChips: List<String> = emptyList(),
    content: @Composable () -> Unit
) {
    AdminCard(modifier, feature = feature, tinted = tinted) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(Modifier.weight(1f)) {
                // Night Arena: feature cards are dark too — titles are always ink.
                val titleColor = AppColors.Ink
                val subtitleColor = if (feature) AppColors.Faint else AppColors.Muted
                SpanText(title, Modifier.displaySmall().color(titleColor))
                if (subtitle != null) {
                    Box(Modifier.height(6.px))
                    SpanText(subtitle, Modifier.bodyMRegular().color(subtitleColor))
                }
            }
            if (actions != null) {
                Row(verticalAlignment = Alignment.CenterVertically) { actions() }
            }
        }
        if (metaChips.isNotEmpty()) {
            Box(Modifier.height(12.px))
            Row(verticalAlignment = Alignment.CenterVertically) {
                metaChips.forEach { chip ->
                    MetaChip(chip, feature = feature)
                    Box(Modifier.width(8.px))
                }
            }
        }
        Box(Modifier.height(20.px))
        Box(Modifier.fillMaxWidth().styleModifier { property("min-height", "240px") }) {
            content()
        }
    }
}

/**
 * Night Arena Section card — glass surface (rgba white .04 + .08 hairline),
 * optional title/subtitle header and actions slot.
 */
@Composable
fun SectionCard(
    title: String? = null,
    subtitle: String? = null,
    modifier: Modifier = Modifier,
    liftOnHover: Boolean = false,
    actions: (@Composable RowScope.() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    Box(
        modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .then(if (liftOnHover) Modifier.classNames("arena-lift") else Modifier)
            .styleModifier {
                property("background", "rgba(255,255,255,0.04)")
                property("border", "1px solid rgba(255,255,255,0.08)")
                property("backdrop-filter", "blur(14px)")
                property("-webkit-backdrop-filter", "blur(14px)")
                property("overflow", "hidden")
            }
    ) {
        Column(Modifier.fillMaxWidth().sPadding(24)) {
            if (title != null || actions != null) {
                Row(
                    Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(Modifier.weight(1f)) {
                        if (title != null) {
                            SpanText(title, Modifier.displaySmall().color(AppColors.Ink))
                        }
                        if (subtitle != null) {
                            Box(Modifier.height(4.px))
                            SpanText(subtitle, Modifier.bodyMRegular().color(AppColors.Muted))
                        }
                    }
                    if (actions != null) {
                        Row(verticalAlignment = Alignment.CenterVertically) { actions() }
                    }
                }
                Box(Modifier.height(20.px))
            }
            content()
        }
    }
}

@Composable
fun MetaChip(text: String, feature: Boolean = false) {
    Box(
        Modifier
            .padding(leftRight = 10.px, topBottom = 4.px)
            .sBorderRadius(999)
            .styleModifier {
                if (feature) {
                    property("background", "rgba(240,244,252,0.06)")
                    property("border", "1px solid rgba(240,244,252,0.10)")
                } else {
                    property("background", "rgba(255,255,255,0.05)")
                    property("border", "1px solid rgba(255,255,255,0.08)")
                }
            }
    ) {
        val textColor = if (feature) Color("#868A91") else Color("#A7ADB9")
        SpanText(text, Modifier.bodySMedium().color(textColor))
    }
}
